import React, { useEffect, useState } from "react";
import { Box, Button, List, ListItem, Tooltip, Typography } from "@mui/material";
import Draggable from "react-draggable";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import asset from "json/assets";
import events from "json/events";
import eventEmitter from "services/event.emitter";
import { useEventEmitter } from "hooks/useEventEmitter";
import useNotiStack from "hooks/useNotistack";
import { useProfileUsername } from "hooks/ProfileDetails";
import { decryptSecretKey, getModalProperties } from "services/utils";
import ModalWrapper from "layout/ModalWrapper/ModalWrapper";
import NoificationModal from "pages/ModalPage/NoificationModal";
import BackdropLoader from "ui/BackdropLoader/BackdropLoader";
import {
  course_gps_vector_map_shift_update,
  course_map_gps_mapping_details,
  course_map_gps_vector_details,
  course_mapping_note_create,
  course_mapping_note_list,
} from "redux/CourseMapSlice";
import popOverStyles from "styles/component/CustomCoursePopOver.module.scss";
import styles from "styles/component/CourseShiftPanel.module.scss";
import { exceedsMaxDelta, formatOffset, isZeroDelta } from "./shiftGeometry";

const SHIFT_POPOVER_TITLE = "Full Course Shift";
const RELOAD_MAX_ATTEMPTS = 3;
const RELOAD_RETRY_DELAY_MS = 1500;

const CourseShiftPanel = ({ shift, mapRef }) => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const id_courseFromQuery = searchParams.get("course");
  const secret_Key = decryptSecretKey();
  const username = useProfileUsername();
  const { toastSuccess, toastError, toastWarning } = useNotiStack();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const modalProperties = getModalProperties();

  const user = { secretKey: secret_Key, userName: username };
  const { phase, delta } = shift;
  const isActive = phase !== "idle";
  const refLat = mapRef?.current?.getCenter?.()?.lat ?? 0;
  const offsetLabel = formatOffset(delta, refLat);
  const isOutOfRange = exceedsMaxDelta(delta);

  useEffect(() => {
    if (isActive) {
      eventEmitter.emit(events.openMapElementInfoModal, {
        visible: true,
        title: SHIFT_POPOVER_TITLE,
      });
      eventEmitter.emit(events.addMarkerToggle, { visible: false });
    }
  }, [isActive]);

  useEventEmitter(events.openMapElementInfoModal, (e) => {
    if (
      e?.visible &&
      e?.title !== SHIFT_POPOVER_TITLE &&
      shift.activeRef.current &&
      shift.phase !== "processing" &&
      shift.phase !== "settling"
    ) {
      setConfirmOpen(false);
      setDiscardOpen(false);
      shift.cancel();
    }
  });

  useEventEmitter(events.escapeFullCourseShift, () => {
    if (
      phase === "processing" ||
      phase === "settling" ||
      confirmOpen ||
      discardOpen
    )
      return;
    if (phase === "reviewing") {
      shift.finish();
      return;
    }
    if (phase === "adjusting") {
      if (isZeroDelta(shift.delta)) {
        shift.cancel();
      } else {
        setDiscardOpen(true);
      }
    }
  });

  const handleCancelRequest = () => {
    if (isZeroDelta(delta)) {
      shift.cancel();
    } else {
      setDiscardOpen(true);
    }
  };

  const refreshCourseData = (
    appliedDelta,
    attempt = 1,
    channels = { vector: true, gps: true }
  ) => {
    const body = { id_course: id_courseFromQuery };
    const requests = [];
    if (channels.vector) {
      requests.push(
        dispatch(course_map_gps_vector_details({ body, user })).then((res) => ({
          channel: "vector",
          ok: Number(res?.payload?.data?.Status) === 1,
        }))
      );
    }
    if (channels.gps) {
      requests.push(
        dispatch(course_map_gps_mapping_details({ body, user })).then(
          (res) => ({
            channel: "gps",
            ok: Number(res?.payload?.data?.Status) === 1,
          })
        )
      );
    }
    Promise.all(requests).then((results) => {
      const failed = results.filter((result) => !result.ok);
      if (!failed.length) {
        shift.beginSettling(appliedDelta);
        return;
      }
      if (attempt < RELOAD_MAX_ATTEMPTS) {
        const retryChannels = {
          vector: failed.some((result) => result.channel === "vector"),
          gps: failed.some((result) => result.channel === "gps"),
        };
        setTimeout(
          () => refreshCourseData(appliedDelta, attempt + 1, retryChannels),
          RELOAD_RETRY_DELAY_MS
        );
        return;
      }
      shift.completeProcessing(appliedDelta);
      toastWarning(
        "The shift was applied, but the course data could not be reloaded. The map may be outdated — refresh the page to verify."
      );
    });
  };

  const createShiftNote = (appliedLabel) => {
    dispatch(
      course_mapping_note_create({
        body: {
          note: `Shifted full course -> ${appliedLabel}`,
          id_course: Number(id_courseFromQuery),
        },
        user,
      })
    )?.then((res) => {
      if (Number(res?.payload?.data?.Status) === 1) {
        dispatch(
          course_mapping_note_list({
            body: { id_course: id_courseFromQuery },
            user,
          })
        );
      } else {
        toastWarning(
          "The course was shifted, but the mapping note could not be created."
        );
      }
    });
  };

  const handleSubmit = () => {
    setConfirmOpen(false);
    const appliedDelta = delta;
    const appliedLabel = formatOffset(appliedDelta, refLat);
    shift.beginProcessing();
    dispatch(
      course_gps_vector_map_shift_update({
        body: {
          idCourseInt: Number(id_courseFromQuery),
          deltaLatitude: appliedDelta.deltaLat,
          deltaLongitude: appliedDelta.deltaLng,
        },
        user,
      })
    ).then((res) => {
      const data = res?.payload?.data;
      if (Number(data?.Status) === 1) {
        toastSuccess("Course shift applied");
        createShiftNote(appliedLabel);
        setTimeout(() => refreshCourseData(appliedDelta), 500);
      } else {
        shift.failProcessing();
        toastError(
          `Course shift failed. ${data?.ErrorMessage ?? "Unknown error"}`
        );
      }
    });
  };

  if (!isActive) return null;

  return (
    <>
      {(phase === "adjusting" || phase === "reviewing") && (
        <Draggable
          bounds={"#DraggableContainer"}
          defaultPosition={{ x: 0, y: 0 }}
          cancel=".MuiSlider-root, button, input"
        >
          <Box
            className={`${popOverStyles.poover_content} ${popOverStyles.default_poover_content} ${styles.shift_panel} custom_popover_content`}
          >
            <Box className={popOverStyles.poover_head}>
              <Typography variant="h5">Full Course Shift</Typography>
              <List>
                <ListItem>
                  <Tooltip title="Close" arrow placement="top">
                    <Button
                      onClick={
                        phase === "adjusting"
                          ? handleCancelRequest
                          : shift.finish
                      }
                    >
                      <img src={asset.close} alt="" />
                    </Button>
                  </Tooltip>
                </ListItem>
              </List>
            </Box>
            <Box
              className={`${popOverStyles.popover_body} ${styles.panel_body}`}
            >
              {phase === "adjusting" && (
                <>
                  <Typography className={styles.offset_readout}>
                    {offsetLabel}
                  </Typography>
                  <Typography className={styles.hint_text}>
                    Drag the map to move the course. Arrow keys move it by 0.1
                    m per press (hold Shift for 1 m steps). Hold Space to pan
                    the map without moving the course.
                  </Typography>
                  {isOutOfRange && (
                    <Typography className={styles.limit_warning}>
                      Maximum shift is 100 m per direction.
                    </Typography>
                  )}
                  <Button
                    className={styles.primary_btn}
                    disabled={isZeroDelta(delta) || isOutOfRange}
                    onClick={() => setConfirmOpen(true)}
                  >
                    APPLY SHIFT
                  </Button>
                  <Button
                    className={styles.action_btn}
                    disabled={isZeroDelta(delta)}
                    onClick={shift.reset}
                  >
                    RESET
                  </Button>
                  <Button
                    className={styles.action_btn}
                    onClick={handleCancelRequest}
                  >
                    CANCEL
                  </Button>
                </>
              )}
              {phase === "reviewing" && (
                <>
                  <Typography className={styles.offset_readout}>
                    Shift applied
                  </Typography>
                  <Typography className={styles.hint_text}>
                    Verify the course position on the map.
                  </Typography>
                  <Button className={styles.primary_btn} onClick={shift.finish}>
                    DONE
                  </Button>
                  <Button
                    className={styles.action_btn}
                    onClick={shift.shiftAgain}
                  >
                    SHIFT AGAIN
                  </Button>
                  <Button
                    className={styles.action_btn}
                    disabled={!shift.lastAppliedDelta}
                    onClick={shift.revertLast}
                  >
                    REVERT LAST SHIFT
                  </Button>
                </>
              )}
            </Box>
          </Box>
        </Draggable>
      )}
      {(phase === "processing" || phase === "settling") && (
        <BackdropLoader open text="Applying course shift..." />
      )}
      <ModalWrapper
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        handleClose={() => setConfirmOpen(false)}
      >
        <NoificationModal
          label="Full Course Shift"
          backgroundColor={modalProperties.info.backgroundColor}
          image={modalProperties.warning.icon}
          title={`<span style='font-size:14px'>Shift the entire course #${id_courseFromQuery}?<br/>Offset: ${offsetLabel}<br/>This will move all course elements (vector map and simple GPS). The shift is applied immediately on the server.</span>`}
          rejCallBack={() => setConfirmOpen(false)}
          affrCallBack={handleSubmit}
          affrBtnTxt={"Continue"}
          rejBtnTxt={"Cancel"}
        />
      </ModalWrapper>
      <ModalWrapper
        open={discardOpen}
        onClose={() => setDiscardOpen(false)}
        handleClose={() => setDiscardOpen(false)}
      >
        <NoificationModal
          label="Full Course Shift"
          backgroundColor={modalProperties.info.backgroundColor}
          image={modalProperties.warning.icon}
          title={`<span style='font-size:14px'>Discard the shift preview?<br/>Current offset: ${offsetLabel}</span>`}
          rejCallBack={() => setDiscardOpen(false)}
          affrCallBack={() => {
            setDiscardOpen(false);
            shift.cancel();
          }}
          affrBtnTxt={"Discard"}
          rejBtnTxt={"Keep adjusting"}
        />
      </ModalWrapper>
    </>
  );
};

export default CourseShiftPanel;
